import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import { getApiData } from "../../utils/APIHelper";
import { color } from "../../config/theme";
import {
  renderCurrencyIcon,
  renderCurrencyIconColored,
} from "../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";
import { toast } from "react-toastify";
import moment from "moment";

// ====================================================================
// Shared text colors — light slate label vs. near-black value, used
// across every row + heading on this page.
// ====================================================================
const LABEL_COLOR = "#949DAA";
const TEXT_COLOR = "#0D0D0E";

// ====================================================================
// Formatting helpers
// ====================================================================
function formatLabel(str) {
  if (!str) return "-";
  return str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Inline "<icon> <amount>" pair, used wherever a currency value is
// shown. Replaces the previous "AED 1,234" string formatting.
const Money = ({ amount, large = false, color: textColor = TEXT_COLOR }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      color: textColor,
      fontFamily: "Inter Tight",
      fontWeight: large ? 700 : 500,
      fontSize: large ? { xs: 24, md: 28 } : 14,
      lineHeight: 1.1,
    }}
  >
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center" }}
    >
      {large
        ? renderCurrencyIconColored("black-small")
        : renderCurrencyIcon("black-xs-small")}
    </Box>
    {formattedAmountWithoutAed(Number(amount ?? 0))}
  </Box>
);

// ====================================================================
// Page
// ====================================================================
export default function ContractorBilling() {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [plansError, setPlansError] = useState(null);

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
  }, []);

  async function fetchSubscription() {
    setSubscriptionLoading(true);
    setSubscriptionError(null);
    try {
      const response = await getApiData(
        "api/v2/subscription/my",
        "GET",
        {},
        {},
        true,
      );
      if (response) {
        setSubscription(response);
      } else {
        setSubscriptionError("Failed to load subscription data.");
      }
    } catch (error) {
      console.log("ContractorBilling ~ fetchSubscription ~ error:", error);
      setSubscriptionError("Failed to load subscription data.");
      toast.error("Failed to load subscription information.");
    } finally {
      setSubscriptionLoading(false);
    }
  }

  async function fetchPlans() {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const response = await getApiData(
        "api/v2/subscription/plans",
        "GET",
        {},
        {},
        true,
      );
      if (Array.isArray(response)) {
        setPlans(response);
      } else {
        setPlansError("Failed to load plans.");
      }
    } catch (error) {
      console.log("ContractorBilling ~ fetchPlans ~ error:", error);
      setPlansError("Failed to load available plans.");
      toast.error("Failed to load available plans.");
    } finally {
      setPlansLoading(false);
    }
  }

  const currentPlanId = subscription?.plan?.id;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        backgroundColor: "#fff",
        fontFamily: "Inter Tight",
      }}
    >
      {/* Gradient header — matches the look from the Change Request,
          Project Details and other pages. Spans full width, no inner
          margin / border-radius. */}
      <Box
        sx={{
          padding: { xs: "2vh 4vw 1vh", md: "3vh 2.5vw 1.5vh" },
          background:
            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: TEXT_COLOR,
            fontFamily: "Inter Tight",
            fontWeight: 500,
            fontSize: { xs: "18px", md: "22px" },
          }}
        >
          Billing & Subscription
        </Typography>
        <Typography
          sx={{
            color: LABEL_COLOR,
            fontFamily: "Inter Tight",
            fontSize: { xs: "12px", md: "13px" },
            mt: 0.5,
          }}
        >
          Manage your active plan and explore available subscription options.
        </Typography>
      </Box>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: { xs: "2vh 4vw 4vh", md: "2vh 2.5vw 4vh" },
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {/* ---------- Active Subscription ---------- */}
        <Box>
          <SectionHeading>Active Subscription</SectionHeading>

          {subscriptionLoading ? (
            <CenteredSpinner minHeight={140} />
          ) : subscriptionError ? (
            <ErrorBanner message={subscriptionError} />
          ) : subscription ? (
            <ActiveSubscriptionCard subscription={subscription} />
          ) : null}
        </Box>

        {/* ---------- Available Plans ---------- */}
        <Box>
          <SectionHeading
            subtitle="Pick the plan that matches your pipeline. Upgrade or downgrade anytime."
          >
            Available Plans
          </SectionHeading>

          {plansLoading ? (
            <CenteredSpinner minHeight={200} />
          ) : plansError ? (
            <ErrorBanner message={plansError} />
          ) : (
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <PlanCard
                    plan={plan}
                    isCurrent={plan.id === currentPlanId}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ====================================================================
// Active Subscription card
// ====================================================================
function ActiveSubscriptionCard({ subscription }) {
  const plan = subscription.plan || {};
  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "16px",
        backgroundColor: "#fff",
        padding: { xs: "20px", md: "24px" },
        boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.04)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: TEXT_COLOR,
              fontFamily: "Inter Tight",
              fontWeight: 600,
              fontSize: { xs: "18px", md: "20px" },
            }}
          >
            {plan.displayName || "-"}
          </Typography>
          <Typography
            sx={{
              color: LABEL_COLOR,
              fontFamily: "Inter Tight",
              fontSize: "13px",
              mt: 0.25,
            }}
          >
            Plan
          </Typography>
        </Box>
        <StatusPill status={subscription.status} />
      </Box>

      <Divider sx={{ borderColor: "#F1F2F4", mb: 2 }} />

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={6} sm={4} md={3}>
          <Stat
            label="Monthly Fee"
            value={
              plan.monthlyFee === 0 ? (
                "Free"
              ) : (
                <>
                  <Money amount={plan.monthlyFee} /> /mo
                </>
              )
            }
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Stat
            label="Transaction Fee"
            value={
              plan.transactionFeePercent != null
                ? `${plan.transactionFeePercent}%`
                : "-"
            }
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Stat
            label="Setup Fee"
            value={
              plan.setupFee === 0 ? (
                "No setup fee"
              ) : (
                <Money amount={plan.setupFee} />
              )
            }
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Stat
            label="Support Type"
            value={formatLabel(plan.supportType)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Stat
            label="Active Projects"
            value={
              subscription.currentActiveProjects != null
                ? String(subscription.currentActiveProjects)
                : "-"
            }
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Stat
            label="Current Annual Value"
            value={<Money amount={subscription.currentAnnualValue} />}
          />
        </Grid>
        <Grid item xs={12} sm={8} md={6}>
          <Stat
            label="Annual Period"
            value={
              subscription.annualPeriodStart && subscription.annualPeriodEnd
                ? `${moment(subscription.annualPeriodStart).format(
                    "MMM D, YYYY",
                  )} — ${moment(subscription.annualPeriodEnd).format(
                    "MMM D, YYYY",
                  )}`
                : "-"
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// ====================================================================
// Plan card (Available Plans)
// ====================================================================
function PlanCard({ plan, isCurrent }) {
  const isRecommended = !!plan.isRecommended;
  const accent = isRecommended ? "#4D6BF6" : TEXT_COLOR;

  return (
    <Box
      sx={{
        position: "relative",
        border: isRecommended
          ? "1.5px solid #4D6BF6"
          : "1px solid #E5E7EB",
        borderRadius: "16px",
        backgroundColor: "#fff",
        padding: { xs: "20px", md: "24px" },
        boxShadow: isRecommended
          ? "0px 4px 16px rgba(77, 107, 246, 0.12)"
          : "0px 1px 3px rgba(16, 24, 40, 0.04)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Floating badge — current OR recommended */}
      {(isCurrent || isRecommended) && (
        <Box
          sx={{
            position: "absolute",
            top: -12,
            right: 16,
            display: "flex",
            gap: 1,
          }}
        >
          {isCurrent ? (
            <Badge
              icon={<CheckIcon sx={{ fontSize: 12 }} />}
              label="CURRENT PLAN"
              bg="#D1FADF"
              fg="#027A48"
            />
          ) : (
            <Badge
              icon={<StarIcon sx={{ fontSize: 12 }} />}
              label="RECOMMENDED"
              bg="#4D6BF6"
              fg="#fff"
            />
          )}
        </Box>
      )}

      {/* Plan name + description */}
      <Box>
        <Typography
          sx={{
            color: TEXT_COLOR,
            fontFamily: "Inter Tight",
            fontWeight: 600,
            fontSize: { xs: "18px", md: "20px" },
          }}
        >
          {plan.displayName}
        </Typography>
        {plan.description && (
          <Typography
            sx={{
              color: LABEL_COLOR,
              fontFamily: "Inter Tight",
              fontSize: "13px",
              mt: 0.5,
            }}
          >
            {plan.description}
          </Typography>
        )}
      </Box>

      {/* Price */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        {plan.monthlyFee === 0 ? (
          <>
            <Money amount={0} large color={accent} />
            <Typography
              sx={{
                color: LABEL_COLOR,
                fontFamily: "Inter Tight",
                fontSize: "14px",
              }}
            >
              forever
            </Typography>
          </>
        ) : (
          <>
            <Money amount={plan.monthlyFee} large color={accent} />
            <Typography
              sx={{
                color: LABEL_COLOR,
                fontFamily: "Inter Tight",
                fontSize: "14px",
              }}
            >
              /mo
            </Typography>
          </>
        )}
      </Box>

      <Divider sx={{ borderColor: "#F1F2F4" }} />

      {/* Detail rows */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          flex: 1,
        }}
      >
        <DetailRow
          label="Transaction Fee"
          value={`${plan.transactionFeePercent}%`}
        />
        <DetailRow
          label="Max Active Projects"
          value={
            plan.isUnlimitedProjects
              ? "Unlimited"
              : String(plan.maxActiveProjects ?? "-")
          }
        />
        <DetailRow
          label="Max Annual Value"
          value={
            plan.isUnlimitedAnnualValue ? (
              "Unlimited"
            ) : plan.maxAnnualValue != null ? (
              <Money amount={plan.maxAnnualValue} />
            ) : (
              "-"
            )
          }
        />
        <DetailRow label="PM Type" value={formatLabel(plan.pmType)} />
        <DetailRow
          label="Estimator Type"
          value={formatLabel(plan.estimatorType)}
        />
        <DetailRow
          label="Support Type"
          value={formatLabel(plan.supportType)}
        />
        <DetailRow
          label="Payment Platform"
          value={plan.paymentPlatform ? "Yes" : "No"}
        />
        <DetailRow
          label="API Access"
          value={plan.apiAccess ? "Yes" : "No"}
        />
        {plan.setupFee > 0 && (
          <DetailRow
            label="Setup Fee"
            value={<Money amount={plan.setupFee} />}
          />
        )}
      </Box>

    </Box>
  );
}

// ====================================================================
// Small shared bits
// ====================================================================
function SectionHeading({ children, subtitle }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          color: TEXT_COLOR,
          fontFamily: "Inter Tight",
          fontWeight: 600,
          fontSize: { xs: "15px", md: "16px" },
        }}
      >
        {children}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            color: LABEL_COLOR,
            fontFamily: "Inter Tight",
            fontSize: "13px",
            mt: 0.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function Stat({ label, value }) {
  return (
    <Box>
      <Typography
        sx={{
          color: LABEL_COLOR,
          fontFamily: "Inter Tight",
          fontSize: "12px",
          fontWeight: 500,
          mb: 0.5,
          textTransform: "uppercase",
          letterSpacing: "0.4px",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          color: TEXT_COLOR,
          fontFamily: "Inter Tight",
          fontSize: "14px",
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          flexWrap: "wrap",
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          color: LABEL_COLOR,
          fontFamily: "Inter Tight",
          fontSize: "13px",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          color: TEXT_COLOR,
          fontFamily: "Inter Tight",
          fontSize: "13px",
          fontWeight: 500,
          textAlign: "right",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function Badge({ icon, label, bg, fg }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        backgroundColor: bg,
        color: fg,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.4px",
        fontFamily: "Inter Tight",
        whiteSpace: "nowrap",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.08)",
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

function StatusPill({ status }) {
  const active = String(status || "").toUpperCase() === "ACTIVE";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        backgroundColor: active ? "#D1FADF" : "#F2F4F7",
        color: active ? "#027A48" : LABEL_COLOR,
        fontFamily: "Inter Tight",
        whiteSpace: "nowrap",
        letterSpacing: "0.2px",
      }}
    >
      {formatLabel(status)}
    </Box>
  );
}

function CenteredSpinner({ minHeight = 140 }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight,
      }}
    >
      <CircularProgress style={{ color: color.primary }} size={28} />
    </Box>
  );
}

function ErrorBanner({ message }) {
  return (
    <Box
      sx={{
        border: "1px solid #FECDCA",
        borderRadius: "10px",
        padding: "16px 18px",
        backgroundColor: "#FFF9F9",
      }}
    >
      <Typography
        sx={{
          color: "#D92D20",
          fontFamily: "Inter Tight",
          fontSize: "14px",
        }}
      >
        {message}
      </Typography>
    </Box>
  );
}
